/**
 * Pricing page — Free + three paid tiers (Core / Plus / Unlimited)
 * side-by-side with a Monthly/Annual toggle that applies per-card.
 *
 *   - Reads `/billing/status` on mount to decide whether the user is
 *     already subscribed (hides paid CTAs for the tier they're on).
 *   - "Free" card is informational only — no checkout, since you
 *     start free by default.
 *   - Unlimited is the highlighted anchor: Plus sits just below it so
 *     "unlimited for a few dollars more" reads as the obvious pick.
 *
 * Server-side webhook handles the actual subscription transition. This
 * page only opens the Stripe Checkout flow.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  createCheckoutSession,
  getBillingStatus,
  recordFunnelEvent,
  type BillingStatus,
  type Plan,
  type PlanLevel,
} from "@/features/billing/lib/billing";

const ANNUAL_SAVINGS_LABEL = "Save 2 months";

interface PaidTier {
  id: PlanLevel;
  name: string;
  monthly: string;
  /** Annual charge shown as a per-month equivalent (annual ÷ 12). */
  annualPerMonth: string;
  annualTotal: string;
  features: string[];
  /** The visually-emphasized anchor tier. */
  highlighted?: boolean;
  badge?: string;
}

const PAID_TIERS: PaidTier[] = [
  {
    id: "core",
    name: "Core",
    monthly: "$9.99",
    annualPerMonth: "$8.33",
    annualTotal: "$99.90",
    features: [
      "300 coach responses per month",
      "Session coaching — warmup, debrief & consult your real play",
      "Ongoing leak reports as your game changes",
      "Everything in Free",
    ],
  },
  {
    id: "plus",
    name: "Plus",
    monthly: "$24.99",
    annualPerMonth: "$20.83",
    annualTotal: "$249.90",
    features: [
      "1,000 coach responses per month",
      "Built for heavy daily grinders",
      "Everything in Core",
    ],
  },
  {
    id: "unlimited",
    name: "Unlimited",
    monthly: "$39.99",
    annualPerMonth: "$33.33",
    annualTotal: "$399.90",
    features: [
      "Unlimited coach responses",
      "Never hit a wall, ever",
      "Everything in Plus",
    ],
    highlighted: true,
    badge: "Best value",
  },
];

export function Pricing() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [plan, setPlan] = useState<Plan>("annual"); // default to annual: best-value framing

  useEffect(() => {
    let cancelled = false;
    getBillingStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setStatusError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Funnel telemetry: viewing pricing is a conversion-funnel stage (the
  // step between hitting the wall and starting checkout). Fire-and-forget;
  // best-effort so it never affects the page.
  useEffect(() => {
    void recordFunnelEvent("pricing_viewed");
  }, []);

  const isAlreadyPaying =
    status?.subscriptionStatus === "active" ||
    status?.subscriptionStatus === "past_due";

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10 space-y-8">
      <header className="space-y-2 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-text tracking-tight">
          Drill harder. Get sharper.
        </h1>
        <p className="text-text-muted text-sm md:text-base">
          Free gets you to your first leak report and your session tracker.
          Subscribe to keep drilling with the coach — and have it debrief your
          real sessions to find what's costing you.
        </p>
        <p className="text-text-muted/70 text-xs">
          A coach response is each time the coach grades a decision or answers
          a question.
        </p>
      </header>

      {statusError && (
        <div className="text-sm text-danger text-center">
          Couldn't load billing status: {statusError}
        </div>
      )}

      {/* Monthly / Annual toggle */}
      <div className="flex justify-center">
        <div className="inline-flex rounded-xl border border-border bg-surface p-1 gap-1">
          <PlanToggleButton
            label="Monthly"
            selected={plan === "monthly"}
            onClick={() => setPlan("monthly")}
          />
          <PlanToggleButton
            label="Annual"
            badge={ANNUAL_SAVINGS_LABEL}
            selected={plan === "annual"}
            onClick={() => setPlan("annual")}
          />
        </div>
      </div>

      {/* Free + three paid tiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        <FreeCard status={status} navigate={navigate} />
        {PAID_TIERS.map((tier) => (
          <TierCard
            key={tier.id}
            tier={tier}
            plan={plan}
            status={status}
            isAlreadyPaying={isAlreadyPaying}
            navigate={navigate}
          />
        ))}
      </div>
    </div>
  );
}

// ===========================================================================
// Tier cards
// ===========================================================================

function FreeCard({
  status,
  navigate,
}: {
  status: BillingStatus | null;
  navigate: (path: string) => void;
}) {
  // For an already-subscribed user, we still show the Free card for
  // information but with no CTA — they can use Stripe Portal to
  // downgrade.
  const isFree = status?.subscriptionStatus === "free";
  return (
    <article className="rounded-2xl border border-border bg-surface/60 p-5 space-y-4 flex flex-col">
      <header>
        <div className="text-xs uppercase tracking-wider text-text-muted/80">
          Free
        </div>
        <div className="flex items-baseline gap-1.5 mt-1">
          <span className="text-3xl font-bold text-text font-mono tabular-nums">
            $0
          </span>
          <span className="text-text-muted text-sm">/ forever</span>
        </div>
      </header>
      <ul className="space-y-1.5 text-sm text-text-muted flex-1">
        <li>• Unlimited drilling up to your first leak report</li>
        <li>• Your first leak report — the coach reads your game</li>
        <li>• Session &amp; bankroll tracker — log results, track your bankroll</li>
        <li>• Then free forever: quizzes, study sheets, your saved report</li>
        <li>• Skill profile + history</li>
      </ul>
      {isFree ? (
        <button
          type="button"
          disabled
          className="w-full py-3 rounded-xl border border-border text-text-muted text-sm font-medium cursor-default"
        >
          You're on Free
        </button>
      ) : (
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="w-full py-3 rounded-xl border border-border text-text-muted text-sm font-medium hover:text-text hover:border-text-muted/40 transition"
        >
          Manage in Settings
        </button>
      )}
    </article>
  );
}

function TierCard({
  tier,
  plan,
  status,
  isAlreadyPaying,
  navigate,
}: {
  tier: PaidTier;
  plan: Plan;
  status: BillingStatus | null;
  isAlreadyPaying: boolean;
  navigate: (path: string) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const isOnThisTier = isAlreadyPaying && status?.planLevel === tier.id;
  // Annual is always displayed as the per-month equivalent so users
  // compare apples-to-apples with the Monthly toggle. The full annual
  // charge is disclosed below as "billed annually."
  const displayPrice = plan === "monthly" ? tier.monthly : tier.annualPerMonth;

  async function startCheckout() {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Promo codes are entered on Stripe's hosted checkout — backend
      // passes allow_promotion_codes=true when no code is provided here.
      const { url } = await createCheckoutSession({ planLevel: tier.id, plan });
      window.location.href = url;
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : String(e));
      setSubmitting(false);
    }
  }

  const shell = tier.highlighted
    ? "rounded-2xl border border-accent/40 bg-surface p-5 space-y-4 shadow-[0_28px_60px_-30px_rgba(124,147,241,0.5)] flex flex-col relative"
    : "rounded-2xl border border-border bg-surface/60 p-5 space-y-4 flex flex-col";

  return (
    <article className={shell}>
      {tier.badge && (
        <div className="absolute -top-2.5 left-5 px-2 py-0.5 rounded-full bg-accent text-white text-[10px] font-semibold uppercase tracking-wider">
          {tier.badge}
        </div>
      )}
      <header>
        <div
          className={[
            "text-xs uppercase tracking-wider font-semibold",
            tier.highlighted ? "text-accent" : "text-text-muted/80",
          ].join(" ")}
        >
          {tier.name}
        </div>
        <div className="flex items-baseline gap-1.5 mt-1">
          <span className="text-3xl font-bold text-text font-mono tabular-nums">
            {displayPrice}
          </span>
          <span className="text-text-muted text-sm">/ month</span>
        </div>
        {plan === "annual" ? (
          <div className="mt-1 space-y-0.5">
            <div className="text-xs text-text-muted">
              Billed annually at{" "}
              <span className="font-mono tabular-nums text-text">
                {tier.annualTotal}
              </span>
            </div>
            <div
              className={[
                "text-xs",
                tier.highlighted ? "text-accent" : "text-text-muted/80",
              ].join(" ")}
            >
              {ANNUAL_SAVINGS_LABEL}
            </div>
          </div>
        ) : null}
      </header>
      <ul className="space-y-1.5 text-sm text-text-muted flex-1">
        {tier.features.map((f) => (
          <li key={f}>• {f}</li>
        ))}
      </ul>

      {submitError && <div className="text-xs text-danger">{submitError}</div>}

      {isOnThisTier ? (
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="w-full py-3 rounded-xl border border-accent/30 text-accent text-sm font-medium hover:border-accent/60 transition"
        >
          You're on {tier.name}: manage
        </button>
      ) : (
        <button
          type="button"
          onClick={startCheckout}
          disabled={submitting || !status?.billingEnabled}
          className={[
            "w-full py-3 rounded-xl text-sm font-medium transition disabled:opacity-50",
            tier.highlighted
              ? "bg-accent text-white hover:bg-accent/90 shadow-[0_1px_0_rgba(255,255,255,0.18)_inset,0_8px_20px_-8px_rgba(124,147,241,0.6)]"
              : "border border-border text-text hover:border-accent/60 hover:bg-accent/5",
          ].join(" ")}
        >
          {submitting
            ? "Opening checkout…"
            : status?.billingEnabled === false
              ? "Billing not yet enabled"
              : "Subscribe"}
        </button>
      )}
    </article>
  );
}

// ===========================================================================
// Helper components
// ===========================================================================

function PlanToggleButton({
  label,
  badge,
  selected,
  onClick,
}: {
  label: string;
  badge?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 rounded-lg text-sm font-medium transition",
        selected
          ? "bg-accent text-white"
          : "text-text-muted hover:text-text",
      ].join(" ")}
    >
      {label}
      {badge && !selected && (
        <span className="ml-2 text-[10px] uppercase tracking-wider text-accent">
          {badge}
        </span>
      )}
    </button>
  );
}
